import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { taskService } from '../services/taskService';
import { Task } from '../types/task';
import { TaskStatus } from '../types/task-status'; // Изменено с '../types/task'
import { Button, TextField, Typography, Box, Paper, CircularProgress, Alert, MenuItem, FormControl, InputLabel, Select, SelectChangeEvent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Edit, Cancel, Save } from '@mui/icons-material'; // Import icons for edit mode buttons
import { UserType } from '../types/auth'; // Import UserType
import { TaskUserDTO } from '../types/TaskUserDTO'; // Импорт TaskUserDTO
import { TaskUserStatus } from '../types/TaskUserStatus'; // Импорт TaskUserStatus
import { User } from '../types/user'; // Импорт User
import { Media } from '../types/media';
import { mediaService } from '../services/mediaService';

const TaskDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { state } = useAuth();
    const authenticatedUser = state.user; // Получаем весь объект AuthUser
    const managerId = authenticatedUser?.type === UserType.MANAGER ? authenticatedUser.manager?.id : authenticatedUser?.user?.id; // Корректно получаем id для менеджера или исполнителя

    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [availableStatuses, setAvailableStatuses] = useState<TaskStatus[]>([]);
    const [taskUsers, setTaskUsers] = useState<TaskUserDTO[]>([]); // Новое состояние для TaskUser
    const [openAssignUserModal, setOpenAssignUserModal] = useState<boolean>(false); // Состояние для модального окна назначения
    const [usersForAssignment, setUsersForAssignment] = useState<User[]>([]); // Пользователи для назначения
    const [selectedUserToAssign, setSelectedUserToAssign] = useState<string>(''); // ID выбранного пользователя для назначения
    const [mediaFiles, setMediaFiles] = useState<Media[]>([]); // Состояние для медиафайлов
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // Новое состояние для выбранных файлов

    // Form states
    const [editTitle, setEditTitle] = useState<string>('');
    const [editDescription, setEditDescription] = useState<string>('');
    const [editPriceInKopecks, setEditPriceInKopecks] = useState<string>('');
    const [editDeadline, setEditDeadline] = useState<string>('');
    const [editEstimatedHours, setEditEstimatedHours] = useState<string>('');
    const [editPriority, setEditPriority] = useState<string>('');
    const [editRequirements, setEditRequirements] = useState<string>('');
    const [editNeededUsers, setEditNeededUsers] = useState<string>('');
    const [editStatus, setEditStatus] = useState<TaskStatus>(TaskStatus.Created);
    const [editCompletionResult, setEditCompletionResult] = useState<string>('');
    const [editReviewComment, setEditReviewComment] = useState<string>('');
    const [editCurrentExecutorId, setEditCurrentExecutorId] = useState<string>('');
    const [editAssignedAt, setEditAssignedAt] = useState<string>('');
    const [editStartedAt, setEditStartedAt] = useState<string>('');
    const [editCompletedAt, setEditCompletedAt] = useState<string>('');
    const [editVerifiedAt, setEditVerifiedAt] = useState<string>('');
    const [editPaidAt, setEditPaidAt] = useState<string>('');

    useEffect(() => {
        if (!id || !managerId) return;

        const fetchTask = async () => {
            try {
                setLoading(true);
                const isPlatformCuratorOrAdmin = authenticatedUser?.manager?.roles?.includes('PlatformCurator') || authenticatedUser?.manager?.roles?.includes('Admin');
                const data = await taskService.getTaskById(id, managerId, isPlatformCuratorOrAdmin);
                setTask(data);
                setMediaFiles(data.media || []);
                // Initialize form states
                setEditTitle(data.title);
                setEditDescription(data.description);
                setEditPriceInKopecks(data.priceInKopecks.toString());
                setEditDeadline(data.deadline ? data.deadline.substring(0, 16) : ''); // Format for datetime-local
                setEditEstimatedHours(data.estimatedHours?.toString() || '');
                setEditPriority(data.priority.toString());
                setEditRequirements(data.requirements || '');
                setEditNeededUsers(data.neededUsers?.toString() || '');
                setEditStatus(data.status);
                setEditCompletionResult(data.completionResult || '');
                setEditReviewComment(data.reviewComment || '');
                setEditCurrentExecutorId(data.currentExecutorId || '');
                setEditAssignedAt(data.assignedAt ? data.assignedAt.substring(0, 16) : '');
                setEditStartedAt(data.startedAt ? data.startedAt.substring(0, 16) : '');
                setEditCompletedAt(data.completedAt ? data.completedAt.substring(0, 16) : '');
                setEditVerifiedAt(data.verifiedAt ? data.verifiedAt.substring(0, 16) : '');
                setEditPaidAt(data.paidAt ? data.paidAt.substring(0, 16) : '');

                const statuses = await taskService.getAvailableTaskStatuses(id, managerId);
                setAvailableStatuses(statuses);

                // Загрузка TaskUsers
                const usersData = await taskService.getTaskUsersByTaskId(id);
                setTaskUsers(usersData);

            } catch (err) {
                setError('Не удалось загрузить детали задачи.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTask();
    }, [id, managerId, authenticatedUser?.manager?.roles]);

    const handleSave = async () => {
        if (!task || !managerId || !id) return;
        try {
            const updatedTask: Task = {
                ...task,
                title: editTitle,
                description: editDescription,
                priceInKopecks: parseInt(editPriceInKopecks),
                deadline: editDeadline || undefined,
                estimatedHours: editEstimatedHours ? parseInt(editEstimatedHours) : undefined,
                priority: parseInt(editPriority),
                requirements: editRequirements || undefined,
                neededUsers: editNeededUsers ? parseInt(editNeededUsers) : undefined,
                completionResult: editCompletionResult || undefined,
                reviewComment: editReviewComment || undefined,
                currentExecutorId: editCurrentExecutorId || undefined,
                assignedAt: editAssignedAt || undefined,
                startedAt: editStartedAt || undefined,
                completedAt: editCompletedAt || undefined,
                verifiedAt: editVerifiedAt || undefined,
                paidAt: editPaidAt || undefined,
            };
            await taskService.updateTask(id, updatedTask);
            setIsEditing(false);
            // Optionally re-fetch task to show latest data
            const refreshedTask = await taskService.getTaskById(id, managerId);
            setTask(refreshedTask);
            setEditTitle(refreshedTask.title);
            setEditDescription(refreshedTask.description);
            setEditPriceInKopecks(refreshedTask.priceInKopecks.toString());
            setEditDeadline(refreshedTask.deadline ? refreshedTask.deadline.substring(0, 16) : '');
            setEditEstimatedHours(refreshedTask.estimatedHours?.toString() || '');
            setEditPriority(refreshedTask.priority.toString());
            setEditRequirements(refreshedTask.requirements || '');
            setEditNeededUsers(refreshedTask.neededUsers?.toString() || '');
            setEditStatus(refreshedTask.status);
            setEditCompletionResult(refreshedTask.completionResult || '');
            setEditReviewComment(refreshedTask.reviewComment || '');
            setEditCurrentExecutorId(refreshedTask.currentExecutorId || '');
            setEditAssignedAt(refreshedTask.assignedAt ? refreshedTask.assignedAt.substring(0, 16) : '');
            setEditStartedAt(refreshedTask.startedAt ? refreshedTask.startedAt.substring(0, 16) : '');
            setEditCompletedAt(refreshedTask.completedAt ? refreshedTask.completedAt.substring(0, 16) : '');
            setEditVerifiedAt(refreshedTask.verifiedAt ? refreshedTask.verifiedAt.substring(0, 16) : '');
            setEditPaidAt(refreshedTask.paidAt ? refreshedTask.paidAt.substring(0, 16) : '');

        } catch (err) {
            setError('Не удалось сохранить задачу.');
            console.error(err);
        }
    };

    const handleStatusChange = async (event: SelectChangeEvent<TaskStatus>) => {
        if (!task || !managerId || !id) return;
        const newStatus = event.target.value as TaskStatus;
        try {
            await taskService.updateTaskStatus(id, newStatus);
            const refreshedTask = await taskService.getTaskById(id, managerId);
            setTask(refreshedTask);
            setEditStatus(newStatus);
        } catch (err) {
            setError('Не удалось обновить статус задачи.');
            console.error(err);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setSelectedFiles(Array.from(event.target.files));
        }
    };

    const handleUploadFiles = async () => {
        if (!id || selectedFiles.length === 0) return;
        setLoading(true);
        try {
            const uploadPromises = selectedFiles.map(file =>
                mediaService.uploadFile(file, undefined, id) // taskEntityId
            );
            const uploadedMedia = await Promise.all(uploadPromises);
            setMediaFiles(prev => [...prev, ...uploadedMedia]);
            setSelectedFiles([]); // Clear selected files after upload
        } catch (err) {
            setError('Не удалось загрузить файлы.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMedia = async (mediaId: string) => {
        if (!id) return;
        setLoading(true);
        try {
            await mediaService.deleteFile(mediaId);
            setMediaFiles(prev => prev.filter(media => media.id !== mediaId));
        } catch (err) {
            setError('Не удалось удалить файл.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => {
        if (task?.organizationId) {
            navigate(`/organization/${task.organizationId}`);
        } else {
            navigate('/dashboard'); // Fallback if no organizationId
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!task) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Задача не найдена.</Alert></Box>;
    }

    const formatDateTime = (isoString?: string) => {
        if (!isoString) return 'Н/Д';
        return new Date(isoString).toLocaleString();
    };

    const formatPhone = (phone: string) => {
        if (!phone) return 'Н/Д';
        // Удаляем все нецифровые символы, кроме последних 10 цифр
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `+7 (${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 8)}-${cleaned.substring(8)}`;
        }
        return phone;
    };

    const handleOpenAssignUserModal = () => {
        setOpenAssignUserModal(true);
        // Fetch users for assignment
        const fetchUsers = async () => {
            if (!managerId) {
                console.error('Manager ID не определен. Невозможно загрузить пользователей для назначения.');
                setUsersForAssignment([]);
                return;
            }
            try {
                const users = await taskService.getUsersNotInTask(id!, managerId);
                setUsersForAssignment(users);
            } catch (err) {
                console.error('Не удалось загрузить пользователей для назначения:', err);
            }
        };
        fetchUsers();
    };

    const handleCloseAssignUserModal = () => {
        setOpenAssignUserModal(false);
        setSelectedUserToAssign('');
    };

    const handleAssignUserConfirm = async () => {
        if (!task || !managerId || !id || !selectedUserToAssign) return;
        try {
            await taskService.assignUserToTask(id, selectedUserToAssign);
            setOpenAssignUserModal(false);
            setSelectedUserToAssign('');
            // Optionally re-fetch task to show latest data
            const refreshedTask = await taskService.getTaskById(id, managerId);
            setTask(refreshedTask);
            // Re-fetch taskUsers to update the table
            const usersData = await taskService.getTaskUsersByTaskId(id);
            setTaskUsers(usersData);
        } catch (err) {
            setError('Не удалось назначить пользователя на задачу.');
            console.error(err);
        }
    };

    const handleTaskUserStatusChange = async (taskUserId: string, newStatus: TaskUserStatus) => {
        if (!task || !managerId || !id) return;
        try {
            await taskService.updateTaskUserStatus(taskUserId, newStatus);
            // Optionally re-fetch taskUsers to update the table
            const usersData = await taskService.getTaskUsersByTaskId(id);
            setTaskUsers(usersData);
        } catch (err) {
            setError('Не удалось обновить статус исполнителя.');
            console.error(err);
        }
    };

    const handleSaveTaskUser = async (taskUserId: string, newStatus: TaskUserStatus) => {
        if (!task || !managerId || !id) return;
        try {
            await taskService.updateTaskUserStatus(taskUserId, newStatus);
            // Optionally re-fetch taskUsers to update the table
            const usersData = await taskService.getTaskUsersByTaskId(id);
            setTaskUsers(usersData);
        } catch (err) {
            setError('Не удалось сохранить статус исполнителя.');
            console.error(err);
        }
    };

    const handleDeleteTaskUser = async (taskUserId: string) => {
        if (!task || !managerId || !id) return;
        if (window.confirm('Вы уверены, что хотите удалить этого исполнителя?')) {
            try {
                await taskService.deleteTaskUser(taskUserId);
                // Optionally re-fetch taskUsers to update the table
                const usersData = await taskService.getTaskUsersByTaskId(id);
                setTaskUsers(usersData);
            } catch (err) {
                setError('Не удалось удалить исполнителя.');
                console.error(err);
            }
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4">Детали Задачи</Typography>
                    <Button variant="outlined" onClick={handleBackClick}>Вернуться к Организации</Button>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                    {!isEditing && (
                        <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Редактировать Задачу</Button>
                    )}
                    {isEditing && (
                        <Box>
                            <Button variant="outlined" startIcon={<Cancel />} onClick={() => {
                                setIsEditing(false);
                                // Revert form states to current task values
                                setEditTitle(task.title);
                                setEditDescription(task.description);
                                setEditPriceInKopecks(task.priceInKopecks.toString());
                                setEditDeadline(task.deadline ? task.deadline.substring(0, 16) : '');
                                setEditEstimatedHours(task.estimatedHours?.toString() || '');
                                setEditPriority(task.priority.toString());
                                setEditRequirements(task.requirements || '');
                                setEditNeededUsers(task.neededUsers?.toString() || '');
                                setEditStatus(task.status);
                                setEditCompletionResult(task.completionResult || '');
                                setEditReviewComment(task.reviewComment || '');
                                setEditCurrentExecutorId(task.currentExecutorId || '');
                                setEditAssignedAt(task.assignedAt ? task.assignedAt.substring(0, 16) : '');
                                setEditStartedAt(task.startedAt ? task.startedAt.substring(0, 16) : '');
                                setEditCompletedAt(task.completedAt ? task.completedAt.substring(0, 16) : '');
                                setEditVerifiedAt(task.verifiedAt ? task.verifiedAt.substring(0, 16) : '');
                                setEditPaidAt(task.paidAt ? task.paidAt.substring(0, 16) : '');
                            }} sx={{ mr: 1 }}>Отмена</Button>
                            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                        </Box>
                    )}
                </Box>

                <Grid container spacing={3}>
                    {/* Task Overview */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Обзор Задачи</Typography>
                            <TextField
                                label="Название"
                                name="title"
                                value={isEditing ? editTitle : task.title}
                                onChange={(e) => setEditTitle(e.target.value)}
                                fullWidth
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Описание"
                                name="description"
                                value={isEditing ? editDescription : task.description}
                                onChange={(e) => setEditDescription(e.target.value)}
                                fullWidth
                                multiline
                                rows={4}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth disabled={!isEditing} sx={{ mb: 2 }}>
                                <InputLabel>Статус</InputLabel>
                                <Select
                                    value={editStatus}
                                    label="Статус"
                                    onChange={handleStatusChange}
                                >
                                    {availableStatuses.map(status => (
                                        <MenuItem key={status} value={status}>{TaskStatus[status]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    </Grid>

                    {/* Details & Schedule */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Детали и Расписание</Typography>
                            <TextField
                                label="Стоимость (в копейках)"
                                name="priceInKopecks"
                                value={isEditing ? editPriceInKopecks : task.priceInKopecks}
                                onChange={(e) => setEditPriceInKopecks(e.target.value)}
                                fullWidth
                                type="number"
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Крайний срок"
                                name="deadline"
                                value={isEditing ? editDeadline : (task.deadline ? task.deadline.substring(0, 16) : '')}
                                onChange={(e) => setEditDeadline(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Оценочные Часы"
                                name="estimatedHours"
                                value={isEditing ? editEstimatedHours : (task.estimatedHours || '')}
                                onChange={(e) => setEditEstimatedHours(e.target.value)}
                                fullWidth
                                type="number"
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Приоритет (1-5)"
                                name="priority"
                                value={isEditing ? editPriority : task.priority}
                                onChange={(e) => setEditPriority(e.target.value)}
                                fullWidth
                                type="number"
                                inputProps={{ min: 1, max: 5 }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Текущий Исполнитель (ID)"
                                name="currentExecutorId"
                                value={isEditing ? editCurrentExecutorId : (task.currentExecutor?.fullName || task.currentExecutorId || 'Не указан')}
                                onChange={(e) => setEditCurrentExecutorId(e.target.value)}
                                fullWidth
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                        </Paper>
                    </Grid>

                    {/* Requirements & Resources */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Требования и Ресурсы</Typography>
                            <TextField
                                label="Требования"
                                name="requirements"
                                value={isEditing ? editRequirements : (task.requirements || '')}
                                onChange={(e) => setEditRequirements(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                             <TextField
                                label="Необходимые Исполнители"
                                name="neededUsers"
                                value={isEditing ? editNeededUsers : (task.neededUsers || '')}
                                onChange={(e) => setEditNeededUsers(e.target.value)}
                                fullWidth
                                type="number"
                                inputProps={{ min: 1 }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                             <TextField
                                label="Дата назначения"
                                name="assignedAt"
                                value={isEditing ? editAssignedAt : (task.assignedAt ? task.assignedAt.substring(0, 16) : '')}
                                onChange={(e) => setEditAssignedAt(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Дата начала"
                                name="startedAt"
                                value={isEditing ? editStartedAt : (task.startedAt ? task.startedAt.substring(0, 16) : '')}
                                onChange={(e) => setEditStartedAt(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                        </Paper>
                    </Grid>

                    {/* Completion & Review */}
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Выполнение и Проверка</Typography>
                            <TextField
                                label="Результат Выполнения"
                                name="completionResult"
                                value={isEditing ? editCompletionResult : (task.completionResult || '')}
                                onChange={(e) => setEditCompletionResult(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Комментарий к Проверке"
                                name="reviewComment"
                                value={isEditing ? editReviewComment : (task.reviewComment || '')}
                                onChange={(e) => setEditReviewComment(e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Дата завершения"
                                name="completedAt"
                                value={isEditing ? editCompletedAt : (task.completedAt ? task.completedAt.substring(0, 16) : '')}
                                onChange={(e) => setEditCompletedAt(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Дата подтверждения"
                                name="verifiedAt"
                                value={isEditing ? editVerifiedAt : (task.verifiedAt ? task.verifiedAt.substring(0, 16) : '')}
                                onChange={(e) => setEditVerifiedAt(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Дата оплаты"
                                name="paidAt"
                                value={isEditing ? editPaidAt : (task.paidAt ? task.paidAt.substring(0, 16) : '')}
                                onChange={(e) => setEditPaidAt(e.target.value)}
                                fullWidth
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: !isEditing }}
                                sx={{ mb: 2 }}
                            />
                        </Paper>
                    </Grid>

                    {/* Metadata */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Метаданные</Typography>
                            <Typography variant="body2" color="text.secondary">Создано: {formatDateTime(task.createdAt)}</Typography>
                            <Typography variant="body2" color="text.secondary">Обновлено: {formatDateTime(task.updatedAt)}</Typography>
                            {task.manager?.phone && (
                                <Typography variant="body2" color="text.secondary">Телефон Менеджера: {formatPhone(String(task.manager.phone))}</Typography>
                            )}
                            {task.organization?.name && (
                                <Typography variant="body2" color="text.secondary">Название Организации: {task.organization.name}</Typography>
                            )}
                            {task.currentExecutor?.fullName && (
                                <Typography variant="body2" color="text.secondary">Текущий Исполнитель: {task.currentExecutor.fullName}</Typography>
                            )}
                            {task.taskUsers && task.taskUsers.length > 0 && (
                                <Typography variant="body2" color="text.secondary">Назначенные Исполнители: {task.taskUsers.length}</Typography>
                            )}
                            {task.media && task.media.length > 0 && (
                                <Typography variant="body2" color="text.secondary">Медиафайлы: {task.media.length}</Typography>
                            )}
                            {task.chats && task.chats.length > 0 && (
                                <Typography variant="body2" color="text.secondary">Чаты: {task.chats.length}</Typography>
                            )}
                            {task.taskSkills && task.taskSkills.length > 0 && (
                                <Typography variant="body2" color="text.secondary">Навыки: {task.taskSkills.length}</Typography>
                            )}
                            {task.taskRequiredSkills && task.taskRequiredSkills.length > 0 && (
                                <Typography variant="body2" color="text.secondary">Необходимые Навыки: {task.taskRequiredSkills.length}</Typography>
                            )}
                        </Paper>
                    </Grid>

                    {/* Media Files */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Медиафайлы</Typography>
                            <Grid container spacing={2}>
                                {mediaFiles.map((mediaItem) => (
                                    <Grid item key={mediaItem.id}>
                                        <Box sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                                            {mediaItem.fileUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                                <img src={mediaItem.fileUrl} alt={mediaItem.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', backgroundColor: '#f0f0f0' }}>
                                                    <Typography variant="caption" align="center">{mediaItem.name}</Typography>
                                                </Box>
                                            )}
                                            {isEditing && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    sx={{ position: 'absolute', top: 0, right: 0, minWidth: 'unset', p: '2px' }}
                                                    onClick={() => handleDeleteMedia(mediaItem.id)}
                                                >X</Button>
                                            )}
                                        </Box>
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>{mediaItem.name}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            {isEditing && (
                                <Box sx={{ mt: 2 }}>
                                    <input
                                        accept="image/*,application/pdf"
                                        style={{ display: 'none' }}
                                        id="upload-media-button"
                                        multiple
                                        type="file"
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="upload-media-button">
                                        <Button variant="contained" component="span">
                                            Загрузить Файл
                                        </Button>
                                    </label>
                                    {selectedFiles.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2">Выбрано файлов: {selectedFiles.length}</Typography>
                                            <Button variant="contained" color="primary" onClick={handleUploadFiles} sx={{ mt: 1 }}>
                                                Добавить Файлы
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Task Users */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Исполнители Задачи ({taskUsers.length})</Typography>
                                <Button variant="contained" onClick={handleOpenAssignUserModal} disabled={!isEditing || !managerId}>Назначить Исполнителя</Button>
                            </Box>
                            {taskUsers.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Нет назначенных исполнителей.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Исполнитель</TableCell>
                                                <TableCell>Статус</TableCell>
                                                <TableCell>Назначен</TableCell>
                                                <TableCell>Обновлен</TableCell>
                                                <TableCell>Действия</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {taskUsers.map((tu) => (
                                                <TableRow key={tu.id}>
                                                    <TableCell>{tu.user?.fullName || tu.user?.phone || tu.userId}</TableCell>
                                                    <TableCell>
                                                        <FormControl size="small" fullWidth>
                                                            <Select
                                                                value={tu.status}
                                                                onChange={(e: SelectChangeEvent<TaskUserStatus>) => handleTaskUserStatusChange(tu.id, e.target.value as TaskUserStatus)}
                                                                disabled={!isEditing} // Редактировать только в режиме редактирования
                                                            >
                                                                {Object.values(TaskUserStatus).map((status) => (
                                                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                    <TableCell>{formatDateTime(tu.assignedAt)}</TableCell>
                                                    <TableCell>{formatDateTime(tu.updatedAt)}</TableCell>
                                                    <TableCell>
                                                        {isEditing && (
                                                            <>
                                                                <Button size="small" onClick={() => handleSaveTaskUser(tu.id, tu.status)} sx={{ mr: 1 }}>Сохранить</Button>
                                                                <Button size="small" color="error" onClick={() => handleDeleteTaskUser(tu.id)}>Удалить</Button>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

            </Paper>

            {/* Assign User Modal */}
            <Dialog open={openAssignUserModal} onClose={handleCloseAssignUserModal}>
                <DialogTitle>Назначить Пользователя на Задачу</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                        <InputLabel>Выберите Пользователя</InputLabel>
                        <Select
                            value={selectedUserToAssign}
                            label="Выберите Пользователя"
                            onChange={(e) => setSelectedUserToAssign(e.target.value as string)}
                        >
                            {usersForAssignment.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                    {user.fullName || `Пользователь ${user.phone}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAssignUserModal}>Отмена</Button>
                    <Button onClick={handleAssignUserConfirm} variant="contained" disabled={!selectedUserToAssign || !managerId}>Назначить</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TaskDetailsPage;
